import { useState, useEffect, useCallback, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '@/hooks/useAuth'
import { projectService, type Project } from '@/services/projectService'
import { HttpError } from '@/lib/httpError'
import LoadingOverlay from '@/components/ui/LoadingOverlay'
import ErrorDisplay from '@/components/ui/ErrorDisplay'
import CustomizableTable, { type TableColumn } from '@/components/ui/CustomizableTable'

export default function PipelineStudioPage() {
  const { t, i18n } = useTranslation(['pipeline-studio', 'common'])
  const { user, refreshAuth } = useAuth()
  const navigate = useNavigate()
  const [projects, setProjects] = useState<Project[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize] = useState(10)
  const [totalItems, setTotalItems] = useState(0)

  // Ref to track latest page value
  const currentPageRef = useRef(currentPage)

  useEffect(() => {
    currentPageRef.current = currentPage
  }, [currentPage])

  const fetchProjects = useCallback(async (page?: number) => {
    setLoading(true)
    setError('')

    const actualPage = page ?? currentPageRef.current
    const startTime = Date.now()

    try {
      const offset = (actualPage - 1) * pageSize
      const response = await projectService.getProjects(offset, pageSize)

      const elapsed = Date.now() - startTime
      const remainingTime = Math.max(0, 2000 - elapsed)

      if (remainingTime > 0) {
        await new Promise(resolve => setTimeout(resolve, remainingTime))
      }

      setProjects(response.projects || [])
      setTotalItems(response.total || 0)
      setCurrentPage(actualPage)
    } catch (err) {
      // Handle 401 errors with automatic token refresh
      if (err instanceof HttpError && err.status === 401) {
        try {
          await refreshAuth()
          // Retry the fetch after token refresh
          return fetchProjects(page)
        } catch (refreshError) {
          // If refresh fails, show error
          const elapsed = Date.now() - startTime
          const remainingTime = Math.max(0, 2000 - elapsed)
          if (remainingTime > 0) {
            await new Promise(resolve => setTimeout(resolve, remainingTime))
          }
          const refreshErrorMessage = refreshError instanceof Error ? refreshError.message : 'errors.sessionExpired'
          if (refreshErrorMessage.startsWith('errors.')) {
            setError(t(refreshErrorMessage, { ns: 'common', defaultValue: refreshErrorMessage }))
          } else {
            setError(refreshErrorMessage)
          }
          setLoading(false)
          return
        }
      }

      const elapsed = Date.now() - startTime
      const remainingTime = Math.max(0, 2000 - elapsed)

      if (remainingTime > 0) {
        await new Promise(resolve => setTimeout(resolve, remainingTime))
      }

      const errorMessage = err instanceof Error ? err.message : 'errors.operationFailed'
      if (errorMessage.startsWith('errors.')) {
        setError(t(errorMessage, { ns: 'common', defaultValue: errorMessage }))
      } else {
        setError(errorMessage)
      }
    } finally {
      setLoading(false)
    }
  }, [pageSize, t, refreshAuth])

  useEffect(() => {
    fetchProjects(1)
  }, [fetchProjects])

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800 border-green-200'
      case 'inactive':
        return 'bg-gray-100 text-gray-800 border-gray-200'
      case 'archived':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200'
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200'
    }
  }

  const formatDate = (dateString: string) => {
    if (!dateString) return '-'
    try {
      const date = new Date(dateString)
      const locale = i18n.language === 'zh' ? 'zh-TW' : 'en-US'

      const formattedDate = date.toLocaleString(locale, {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false
      })

      return formattedDate
    } catch {
      return dateString
    }
  }

  const tableColumns: TableColumn<Project>[] = [
    {
      id: 'name',
      label: t('project.name', 'Project Name'),
      accessor: 'name',
      sortable: true,
      searchable: true,
      alwaysVisible: true,
      render: (value, project) => (
        <button
          onClick={() => navigate(`/projects/${project.id}/products/pipeline-studio`)}
          className="text-sm font-semibold text-primary hover:text-primary-dark hover:underline cursor-pointer text-left"
        >
          {value}
        </button>
      )
    },
    {
      id: 'description',
      label: t('project.description', 'Description'),
      accessor: 'description',
      sortable: true,
      searchable: true,
      render: (value) => (
        <div className="text-sm text-gray-600 max-w-md truncate">{value || '-'}</div>
      )
    },
    {
      id: 'owner',
      label: t('project.owner', 'Owner'),
      accessor: (project) => project.owner?.name || '-',
      sortable: true,
      render: (_, project) => (
        <div>
          <div className="text-sm font-medium text-gray-900">{project.owner?.name || '-'}</div>
          <div className="text-sm text-gray-500">{project.owner?.account || '-'}</div>
        </div>
      )
    },
    {
      id: 'status',
      label: t('project.status', 'Status'),
      accessor: 'status',
      sortable: true,
      filterable: true,
      filterOptions: ['active', 'inactive', 'archived'],
      render: (value) => (
        <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold border ${getStatusColor(value)}`}>
          {String(t(`status.${value}`, value))}
        </span>
      )
    },
    {
      id: 'created_at',
      label: t('project.createdAt', 'Created At'),
      accessor: 'created_at',
      sortable: true,
      defaultVisible: false,
      sortValue: (project) => new Date(project.created_at).getTime(),
      render: (value) => (
        <div className="text-sm text-gray-600">{formatDate(value)}</div>
      )
    }
  ]

  return (
    <div className="absolute inset-0">
      {/* Full Screen Loading Overlay */}
      {loading && !error && <LoadingOverlay message={t('loading', 'Loading projects...')} fullScreen />}

      {/* Full Screen Error Display */}
      {error && !loading && (
        <ErrorDisplay
          error={error}
          title={t('errors.title', 'Error')}
          onRefresh={() => fetchProjects()}
          fullScreen
        />
      )}

      {/* Content Container */}
      <div className="max-w-7xl mx-auto px-6 pt-6 pb-4 h-full flex flex-col overflow-y-auto">
        {/* Header */}
        <div className="mb-4 shrink-0">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-text-dark">{t('title', 'Pipeline Studio')}</h1>
              <p className="text-gray-600 mt-2">{t('description', 'Select a project to manage pipelines')}</p>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => fetchProjects()}
                disabled={loading}
                className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <svg className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                <span>{t('actions.refresh', 'Refresh')}</span>
              </button>
            </div>
          </div>
        </div>

        {/* Content - CustomizableTable */}
        {!error && (
          <CustomizableTable
            data={projects}
            columns={tableColumns}
            loading={loading}
            emptyMessage={t('empty.noProjectsJoined', 'You have not joined any projects yet')}
            pagination={{
              currentPage,
              totalItems,
              pageSize,
              onPageChange: fetchProjects
            }}
          />
        )}
      </div>
    </div>
  )
}
